import React from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { FallbackUI } from './FallbackUI';

export const AboutSection: React.FC = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();

  const team = t.about.team;

  if (!team || team.length === 0) {
    return <FallbackUI message="About section loading..." />;
  }

  return (
    <section id="about" className="relative py-24 md:py-32 bg-vilarias-black overflow-hidden">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-vilarias-accent/5 to-transparent opacity-50" />

      <div className="section-padding relative z-10" ref={ref}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Visual side */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="aspect-[4/5] rounded-2xl overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-br from-vilarias-dark to-vilarias-card" />
              <div className="absolute inset-0 bg-hero-glow" />

              {/* Abstract luxury visual representation */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-full h-full">
                  <div className="absolute top-1/4 left-1/4 w-32 h-32 border border-vilarias-accent/30 rounded-full animate-pulse" />
                  <div className="absolute bottom-1/3 right-1/4 w-48 h-48 border border-vilarias-glow/20 rounded-full" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border border-white/5 rounded-full" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-serif text-6xl text-white/10 tracking-widest">V</span>
                  </div>
                </div>
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-vilarias-black via-transparent to-transparent" />
            </div>

            {/* Floating badge */}
            <motion.div
              className="absolute -bottom-6 -right-6 glass-card rounded-xl p-4 border border-vilarias-accent/30"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <p className="text-2xl font-serif text-white">VILARIAS</p>
              <p className="text-xs text-vilarias-accent tracking-wider uppercase">Tenerife</p>
            </motion.div>
          </motion.div>

          {/* Text side */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="font-serif text-3xl md:text-5xl text-white mb-6">
              {t.about.headline}
            </h2>
            <div className="h-px w-16 bg-vilarias-accent mb-8" />

            <p className="text-vilarias-text-secondary leading-relaxed mb-8">
              {t.about.description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {team.map((member, index) => (
                <motion.div
                  key={member}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.4 + index * 0.05 }}
                  className="flex items-center gap-2"
                >
                  <Check size={14} className="text-vilarias-accent flex-shrink-0" />
                  <span className="text-sm text-vilarias-text-secondary">{member}</span>
                </motion.div>
              ))}
            </div>

            <div className="glass-card rounded-xl p-6 border-l-2 border-vilarias-accent">
              <p className="text-white italic leading-relaxed">
                {t.about.mission}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
